import React from 'react';
import { equals, find } from 'ramda';
import PropTypes from 'prop-types';
import { Grid } from 'react-flexbox-grid';

import Paper from 'components/Paper';

import ProfileHeader from './ProfileHeader';
import ProfileEditable from './ProfileEditable';
import Form from './Form';
import createFetcher from 'lib/createFetcher';
import getCurrentJWT from 'lib/jwtHandling';

// Phone data is received from the server in this format: ["formatted number", "locale"]
const parsePhoneValue = value => {
  return Array.isArray(value) ? value[0] : undefined;
};

// Fetch to post new info
const postContactInfo = (type, data) => {
  if (equals(data, ['']) || !data || data.length === 0 || !type || type.length === 0) return;
  return createFetcher(`/api/vault/v2/contactinfo`, {
    method: 'POST',
    body: JSON.stringify({
      contactType: type,
      contactValue: data,
    }),
  });
};

const deleteContactInfo = (userId, orgId, contactType) =>
  createFetcher(
    `/api/vault/v2/contactinfo/${orgId}/${userId}?contactType=${contactType}`,
    {
      method: 'DELETE',
    },
    {},
    false,
  );

const compareAndPatch = (initialData, newData, updateF) => {
  if (equals(initialData, newData)) return;

  // Deleting a field handled here
  // if previous data is found and new data is empty, send delete request
  const newDataEmptyValues = newData.filter(
    n => n && (n.contactValue === '' || (Array.isArray(n.contactValue) && n.contactValue.length === 0)),
  );
  const isEmptied = newDataEmptyValues.filter(
    d =>
      d &&
      !equals(
        d.contactValue,
        initialData && initialData.find(init => init.contactType === d.contactType)
          ? initialData.find(init => init.contactType === d.contactType).contactValue
          : d.contactType === 'sms'
            ? ''
            : [''],
      ),
  );
  const emptiedPromiseArr = isEmptied.map(em => deleteContactInfo(em.userId, orgid, em.contactType));

  // Adding new data and patching a field handled here
  const { orgid } = getCurrentJWT();
  const newArray = [];
  const deleteAndPatch = [];

  newData.forEach(item => {
    const initialItem =
      initialData && find(d => d.userId === item.userId && d.contactType === item.contactType, initialData);

    if (!initialItem) newArray.push(postContactInfo(item.contactType, item.contactValue)); // Create new contact info

    // previous data found and that one is not emptied
    if (
      initialItem &&
      !isEmptied.some(ie => ie.contactType === initialItem.contactType && ie.userId === initialItem.userId)
    ) {
      // Send delete request for prev data and then make new post request for the updated value
      if (equals(item.contactValue, initialItem.contactValue)) return;
      deleteAndPatch.push(
        deleteContactInfo(initialItem.userId, orgid, initialItem.contactType).then(() => {
          return postContactInfo(item.contactType, item.contactValue);
        }),
      );
    }
  });

  Promise.all([...emptiedPromiseArr, ...newArray, ...deleteAndPatch])
    .then(() => {
      updateF();
    })
    .catch(res => console.log(res));
};

class Profile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      initialValues: {},
      formValues: null,
      editing: false,
      avatar: null,
    };
  }

  setInitialValues = formValues => {
    this.setState({
      initialValues: { ...this.state.initialValues, ...formValues },
    });
  };

  fetchContactInfoAndUpdate = () => {
    createFetcher('/api/vault/v2/contactinfo').then(res => {
      this.setInitialValues({
        contactInfo: res.map(r =>
          r.contactType === 'sms' ? { ...r, contactValue: parsePhoneValue(r.contactValue) } : r,
        ),
      });
    });
  };

  componentDidMount() {
    // Get contact information (phone numbers, email addresses)
    this.fetchContactInfoAndUpdate();
  }

  componentDidUpdate(_, prevState) {
    if (this.state.editing !== prevState.editing && this.state.editing === false) {
      if (this.state.formValues && !equals(this.state.formValues.contactInfo, this.state.initialValues.contactInfo)) {
        // Post/modify contact information and fetch new initialstate
        compareAndPatch(
          this.state.initialValues.contactInfo,
          this.state.formValues.contactInfo,
          this.fetchContactInfoAndUpdate,
        );
      }
    }
  }

  toggleEdit = () => this.setState({ editing: !this.state.editing });

  editValues = formValues => this.setState({ formValues });

  render() {
    const { initialValues, editing, avatar } = this.state;
    const { names } = this.props;
    return (
      <div>
        <Paper topMargin={false}>
          <Grid fluid>
            <ProfileHeader
              names={names}
              localeId="profile.title"
              infoText="profile.infoText"
              startEdit={this.toggleEdit}
              editing={editing}
            />
          </Grid>
          {editing && (
            <ProfileEditable name={names[0]} setValues={this.editValues} initial={initialValues}>
              <Form />
            </ProfileEditable>
          )}
          {!editing && <Form formValues={{ ...initialValues }} editing={false} condensedView={false} avatar={avatar} />}
        </Paper>
      </div>
    );
  }
}

Profile.propTypes = {
  names: PropTypes.array,
};

export default Profile;
